import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader2, Save, UploadCloud, Wand2 } from 'lucide-react';
import { apiService } from '@/services/api.service';
import styles from './PackFileEditor.module.scss';
import { usePackPublishLabels } from './usePackPublishLabels';

interface PackFileEditorProps {
  groupId: string;
  /** Chemin (relatif au groupe) du fichier à éditer. */
  path: string;
  /** Notifie le parent après un enregistrement réussi (pour rafraîchir la liste). */
  onSaved?: () => void;
}

/** Éditeur JSON d'un fichier de pack : charge, valide, enregistre, publie (commit/push). */
export function PackFileEditor({ groupId, path, onSaved }: PackFileEditorProps) {
  const [text, setText] = useState('');
  const [original, setOriginal] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const publishLabels = usePackPublishLabels();
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFeedback(null);
    apiService.getPackFileJson(groupId, path).then((res) => {
      if (!active) return;
      const pretty = res.data !== undefined && res.data !== null ? JSON.stringify(res.data, null, 2) : '';
      setText(pretty);
      setOriginal(pretty);
      setJsonError(null);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [groupId, path]);

  const onChange = (value: string) => {
    setText(value);
    try {
      if (value.trim()) JSON.parse(value);
      setJsonError(null);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'JSON invalide');
    }
  };

  const format = () => {
    try {
      setText(JSON.stringify(JSON.parse(text), null, 2));
      setJsonError(null);
    } catch {
      /* ignore : déjà signalé */
    }
  };

  const save = async () => {
    if (jsonError) return;
    setSaving(true);
    setFeedback(null);
    const res = await apiService.savePackFile(groupId, path, text);
    setSaving(false);
    if (res.data?.success) {
      setOriginal(text);
      setFeedback({ ok: true, msg: res.data.message });
      onSaved?.();
    } else {
      setFeedback({ ok: false, msg: res.data?.message || res.error || "Échec de l'enregistrement" });
    }
  };

  const publish = async () => {
    setPublishing(true);
    setFeedback(null);
    const res = await apiService.publishPackGroup(groupId);
    setPublishing(false);
    if (res.data) {
      setFeedback({ ok: res.data.success, msg: res.data.messages.join(' · ') });
    } else {
      setFeedback({ ok: false, msg: res.error || 'Échec de la publication' });
    }
  };

  const dirty = text !== original;

  if (loading) {
    return (
      <div className={styles.loading}>
        <Loader2 size={18} className={styles.spin} /> Chargement du fichier…
      </div>
    );
  }

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar}>
        <code className={styles.path}>{path}</code>
        <button className={styles.toolBtn} onClick={format} disabled={!!jsonError} title="Reformater le JSON">
          <Wand2 size={14} /> Format
        </button>
      </div>

      <textarea
        className={`${styles.area} ${jsonError ? styles.areaError : ''}`}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        wrap="off"
      />

      {jsonError && (
        <div className={styles.error}>
          <AlertTriangle size={14} /> {jsonError}
        </div>
      )}

      {feedback && (
        <div className={feedback.ok ? styles.ok : styles.error}>
          {feedback.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {feedback.msg}
        </div>
      )}

      <div className={styles.actions}>
        <button className={styles.saveBtn} onClick={save} disabled={saving || !!jsonError || !dirty}>
          {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />}
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button className={styles.publishBtn} onClick={publish} disabled={publishing || dirty} title={publishLabels.title}>
          {publishing ? <Loader2 size={14} className={styles.spin} /> : <UploadCloud size={14} />}
          {publishing ? publishLabels.busy : publishLabels.idle}
        </button>
        {dirty && !jsonError && <span className={styles.hint}>Enregistre avant de publier</span>}
      </div>
    </div>
  );
}
